import { createSlice } from "@reduxjs/toolkit";
import types from "../../Actions/NonDodIcApplication/types";
import GlobalTypes from "../../Actions/types";
import AppTypes from "../../Actions/Application/types";
import { NonDodIcApplicationMetadata } from "./NonDodIcApplicationMetadata";
import { NonDodIcPostSurveyMetadata } from "./NonDodIcPostSurveyMetadata";
import { spouseProfileMetadata } from "../SpouseApplication/spouseProfileMetadata"
import { NonDodIcPreSurveyMetadata } from "./NonDodIcPreSurveyMetadata";
import { NonDodIcProfileMetadata } from "./NonDodIcProfileMetadata";
import * as utils from "../../Utils/reducerUtils";

const initialState = {
    showErrors: false,
    applicationMetadata: NonDodIcApplicationMetadata, // Default to Non-DOD IC application metadata
    profileMetadata: NonDodIcProfileMetadata,
    preSurveyMetadata: NonDodIcPreSurveyMetadata,
    postSurveyMetadata: NonDodIcPostSurveyMetadata,
    initialized: false,
    userWillReLocate: false,
    submittingApplication: false,
    saving: false,
    application: {},
    applicationIsComplete: false,
    interests: [],
    fetchingInterests: false,
    spouseProfileMetadata: NonDodIcProfileMetadata,
    nonDodIcApplication: {}, // State for Non-DOD IC application
    nonDodIcApplicationIsComplete: false,
};

const nonDodIcSlice = createSlice({
  name: "nonDodIcApplication",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.SUBMITTED_NON_DOD_IC_APPLICATION, (state) => {
      state.submittingApplication = false;
    });
    builder.addCase(GlobalTypes.LOGOUT, () => ({
      showErrors: false,
      applicationMetadata: NonDodIcApplicationMetadata,
      profileMetadata: NonDodIcProfileMetadata,
      preSurveyMetadata: NonDodIcPreSurveyMetadata,
      postSurveyMetadata: NonDodIcPostSurveyMetadata,
      initialized: false,
      userWillReLocate: false,
      submittingApplication: false,
      saving: false,
      application: {},
      applicationIsComplete: false,
      interests: [],
      fetchingInterests: false,
      spouseProfileMetadata: NonDodIcProfileMetadata,
      nonDodIcApplication: {},
      nonDodIcApplicationIsComplete: false,
    }));
    builder.addCase(types.TOGGLE_NON_DOD_IC_UNFILLED, (state) => {
      state.showErrors = true;
      state.applicationMetadata = state.applicationMetadata.map((input) => ({
        ...input,
        steps: input.steps.map((step) =>
          !step.filled ? { ...step, unseenError: true } : step
        ),
      }));
      state.profileMetadata = state.profileMetadata.map((input) => ({
        ...input,
        steps: input.steps.map((step) =>
          !step.filled ? { ...step, unseenError: true } : step
        ),
      }));
    });
    builder.addCase(types.SUBMIT_NON_DOD_IC_APPLICATION, (state) => {
      state.showErrors = false;
      state.submittingApplication = true;
    });
    builder.addCase(types.INIT_NON_DOD_IC_APPLICATION, (state, action) => {
      const updatedMetaData = utils.processNavOptionsForApplication(
        action.data.application,
        state.applicationMetadata
      );
      const metaData1 =
        action.data.application && action.data.application.is_spouse === 1
          ? state.spouseProfileMetadata || NonDodIcProfileMetadata
          : state.application && state.application.profileMetadata
          ? state.application.profileMetadata
          : NonDodIcProfileMetadata;
      const updatedProfileMetaData = utils.processNavOptionsforProfile(
        action.data.application,
        metaData1
      );
      state.application = action.data.application;
      state.applicationMetadata = updatedMetaData;
      state.applicationIsComplete = utils.allFormsFilled(updatedMetaData);
      state.profileIsComplete = utils.allFormsFilled(updatedProfileMetaData);
      state.initialized = true;
      state.profileMetadata = updatedProfileMetaData;
      state.userWillReLocate =
        action.data.application.address &&
        action.data.application.address.willLocate === "Yes";
    });
    builder.addCase(types.FETCHED_NON_DOD_IC_APPLICATION, (state, action) => {
      const updatedNonDodIcApplicationMetaData =
        utils.processNavOptionsForApplication(
          action.data.application,
          state.applicationMetadata
        );
      const updatedNonDodIcProfileData = utils.processNavOptionsforProfile(
        action.data.application,
        state.profileMetadata
      );
      state.nonDodIcApplication = action.data.application;
      state.applicationMetadata = updatedNonDodIcApplicationMetaData;
      state.saving = false;
      state.nonDodIcApplicationIsComplete = utils.allFormsFilled(
        updatedNonDodIcApplicationMetaData
      );
      state.profileIsComplete = utils.allFormsFilled(updatedNonDodIcProfileData);
      state.profileMetadata = updatedNonDodIcProfileData;
      state.currentInputData = null;
      state.userWillReLocate =
        action.data.application.address &&
        action.data.application.address.willLocate === "Yes";
    });
    builder.addCase(types.SET_NON_DOD_IC_APPLICATION_INPUT_DATA, (state, action) => {
      state.currentInputData = action.data.inputData;
    });
    builder.addCase(GlobalTypes.LOGIN, (state) => {
      state.initialized = false;
      state.showErrors = false;
    });
    builder.addCase(types.EDIT_NON_DOD_IC_APPLICATION, (state) => {
      state.currentInputData = null;
      state.saving = false;
    });
    // Note: FETCHED_NON_DOD_IC_APPLICATION already handled above for nonDodIcApplication
    builder.addCase(types.SAVING_NON_DOD_IC_APPLICATION, (state) => {
      state.saving = true;
    });
    builder.addCase(types.WILL_NON_DOD_IC_RELOCATE_CHANGED, (state, action) => {
      const updatedMeta = utils.processNavOptionsForApplication(
        state.application,
        state.applicationMetadata
      );
      const metaData3 =
        action.data.application && action.data.application.is_spouse === 1
          ? state.spouseProfileMetadata || NonDodIcProfileMetadata
          : state.application && state.application.profileMetadata
          ? state.application.profileMetadata
          : NonDodIcProfileMetadata;
      const updatedProfileMeta = utils.processNavOptionsforProfile(
        state.application,
        metaData3
      );
      state.userWillReLocate = action.data.value;
      state.applicationMetadata = updatedMeta;
      state.profileMetadata = updatedProfileMeta;
    });
    builder.addCase(types.UPDATE_INTERESTS, (state, action) => {
      state.interests = action.data.interests;
    });
    builder.addCase(types.TOGGLE_FETCHING_INTERESTS, (state, action) => {
      state.fetchingInterests = action.data.toggle;
    });
    builder.addCase(AppTypes.UPDATE_LIKERT_SPOUSE, (state, action) => {
      const { userField, value } = action.payload;
      const fieldArray = userField.split(".");
      let currentState =
        state.spouseApplication && state.spouseApplication.application
          ? state.spouseApplication.application
          : state.application;
      if (!currentState) return;
      for (let i = 0; i < fieldArray.length - 1; i++) {
        currentState = currentState[fieldArray[i]];
        if (!currentState) return;
      }
      currentState[fieldArray[fieldArray.length - 1]] = value;
    });
  },
});

export default nonDodIcSlice.reducer;
